import Alpine from 'alpinejs';
import { Layout } from '@/stores/layout_switcher/types';
import { parseLayoutToNumber } from '@/stores/layout_switcher/utils';
import { debounce } from '@/utils/timing';
import { isAgeVerificationRequired } from '@/utils/age_verification';
import { getRandomElement } from './utils';
import { DeviceStore } from './types';
import { DELAY_CONFIG } from './config';

export class VideoList {
    private canPlay: boolean;
    private debounceOnHover: (id: string) => void;
    private debounceOnScroll: () => void;
    private debouncePlay: () => void;
    private idOfPlayingVideo: string;
    private intersectingIds: string[];
    private hideFromIndex: number;
    private isHoverInProgress: boolean;
    // @ts-ignore
    // both of these properties are used in the html, but not in js, had to add the ts-ignore
    private elementHasToWait: string;
    $el: HTMLElement;

    init() {
        this.canPlay = false;
        this.debounceOnHover = debounce(this.initHover.bind(this), DELAY_CONFIG.HOVER);
        this.debounceOnScroll = debounce(this.initAutoPlay.bind(this), DELAY_CONFIG.SCROLL);
        this.debouncePlay = debounce(this.initAutoPlay.bind(this), DELAY_CONFIG.PLAY);
        this.idOfPlayingVideo = '';
        this.intersectingIds = [];
        this.elementHasToWait = '';
        this.hideFromIndex = this.$el.querySelectorAll('.js-video-item').length; // don't hide anything by default
        this.addEventListeners();
        this.debouncePlay();
    }

    destroy(): void {
        this.removeEventListeners();
    }

    addEventListeners(): void {
        window.addEventListener('scroll', this.debounceOnScroll);
    }

    handlePlayEnd(): void {
        this.idOfPlayingVideo = '';
        this.debouncePlay();
    }

    handleVideoIntersect(id: string, isIntersecting: boolean): void {
        if (isIntersecting) {
            if (!this.intersectingIds.includes(id)) {
                this.intersectingIds.push(id);
            }
        } else {
            this.intersectingIds = this.intersectingIds.filter((item) => item !== id);
        }
    }

    initHover(id: string): void {
        this.isHoverInProgress = true;
        if (this.idOfPlayingVideo) {
            this.resetVideo(this.idOfPlayingVideo);
        }

        this.idOfPlayingVideo = id;
        this.playVideo(id);
    }

    handleOnHover(id: string): void {
        this.debounceOnHover(id);
    }

    handleHoverOut(id: string): void {
        this.isHoverInProgress = false;
        this.resetVideo(id);
        this.idOfPlayingVideo = '';
        if (!(<DeviceStore>Alpine.store('device')).isMobile()) {
            this.debouncePlay();
        }
    }

    handleLayoutChange(newLayout: Layout): void {
        if (this.$el.dataset.hideLastRow === 'true') {
            const videoItems = this.$el.querySelectorAll('.js-video-item');
            const startingLayoutCount = parseLayoutToNumber(newLayout);
            const itemsToHide = videoItems.length % startingLayoutCount;
            this.hideFromIndex =
                startingLayoutCount > videoItems.length ? videoItems.length : videoItems.length - itemsToHide;

            if (this.$el.dataset.withAds === 'true' && this.hideFromIndex > videoItems.length) {
                const lastVideoItem = videoItems[videoItems.length - 1];

                if (newLayout === 'four-col') {
                    lastVideoItem.classList.add('1-5xl:hidden');
                    lastVideoItem.classList.remove('4xl:hidden');
                } else if (newLayout === 'six-col') {
                    lastVideoItem.classList.add('4xl:hidden');
                    lastVideoItem.classList.remove('1-5xl:hidden');
                } else {
                    lastVideoItem.classList.remove('1-5xl:hidden');
                    lastVideoItem.classList.remove('4xl:hidden');
                }
                return;
            }

            videoItems.forEach((item, index) => {
                if (index >= this.hideFromIndex) {
                    item.classList.add('hidden');
                } else {
                    item.classList.remove('hidden');
                }
            });
        }
    }

    playVideo(id: string): void {
        if (isAgeVerificationRequired()) {
            return;
        }

        const video = document.getElementById(id) as HTMLVideoElement;
        if (!video || !video.querySelector('source')) return;

        const videoSrc = video.querySelector('source')!;
        video.src = videoSrc.dataset.src!;
        video.load();
        video.play();

        setTimeout(() => {
            if (!this.canPlay) {
                this.elementHasToWait = id;
            }
        }, DELAY_CONFIG.TIMEOUT);
    }

    resetVideo(id: string): void {
        const video = document.getElementById(id) as HTMLVideoElement | null;
        if (!video) return;
        this.canPlay = false;
        this.elementHasToWait = '';
        video.pause();
        video.currentTime = 0;
    }

    removeEventListeners(): void {
        window.removeEventListener('scroll', this.debounceOnScroll);
    }

    initAutoPlay(): void {
        if ((<DeviceStore>Alpine.store('device')).isMobile() || this.isHoverInProgress) {
            return;
        }

        if (this.idOfPlayingVideo && this.intersectingIds.includes(this.idOfPlayingVideo)) {
            return;
        }

        let arrayOfVideoIds = [...this.intersectingIds];

        if (this.idOfPlayingVideo) {
            this.resetVideo(this.idOfPlayingVideo);
            arrayOfVideoIds = arrayOfVideoIds.filter((id) => id !== this.idOfPlayingVideo);
        }

        const id = getRandomElement(arrayOfVideoIds);
        this.idOfPlayingVideo = id;
        this.playVideo(id);
    }

    setCanPlay(): void {
        this.canPlay = true;
        this.elementHasToWait = '';
    }

    error(id: string): void {
        const videoSrc = document.getElementById(id)?.querySelector('source')!;
        const dataSourceValue = videoSrc.getAttribute('data-src');

        // stop the replace chain if we ran into an error on the last video (t.mp4)
        if (dataSourceValue.includes('/t.mp4')) {
            this.setCanPlay();
            return;
        }

        videoSrc.dataset.src = videoSrc.dataset.src.replace('/tm.mp4', '/t.mp4').replace('/td.mp4', '/tm.mp4');
        videoSrc.removeEventListener('error', () => this.error(id));

        this.playVideo(id);
    }
}
